import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { UsersThree, Star } from '@phosphor-icons/react';

const maskDate = (v) => {
  const n = v.replace(/\D/g, '').slice(0, 8);
  if (n.length <= 2) return n;
  if (n.length <= 4) return `${n.slice(0, 2)}/${n.slice(2)}`;
  return `${n.slice(0, 2)}/${n.slice(2, 4)}/${n.slice(4)}`;
};
const isValidIt = (v) => /^\d{2}\/\d{2}\/\d{4}$/.test(v);
const toIso = (v) => (isValidIt(v) ? v.split('/').reverse().join('-') : '');
const fmtIt = (d) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
const sixMonthsAgo = () => { const d = new Date(); d.setMonth(d.getMonth() - 6); return fmtIt(d); };

const punctualityTone = (p) => {
  if (p === null || p === undefined) return 'bg-muted text-foreground/60';
  if (p >= 90) return 'bg-emerald-100 text-emerald-800';
  if (p >= 70) return 'bg-amber-100 text-amber-800';
  return 'bg-red-100 text-red-800';
};

export const CenterStaffAnalytics = () => {
  const [from, setFrom] = useState(sixMonthsAgo());
  const [to, setTo] = useState(fmtIt(new Date()));
  const [range, setRange] = useState({ from: sixMonthsAgo(), to: fmtIt(new Date()) });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (toIso(range.from)) p.set('date_from', toIso(range.from));
      if (toIso(range.to)) p.set('date_to', toIso(range.to));
      const r = await api.get(`/api/center/staff/analytics?${p.toString()}`);
      setData(r.data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals;

  return (
    <Card className="rounded-2xl p-5" data-testid="center-staff-analytics">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-5">
        <div className="flex items-center gap-2">
          <UsersThree size={22} className="text-primary" weight="duotone" />
          <div>
            <p className="font-heading font-medium">Analytics Organico</p>
            <p className="text-xs text-foreground/60">
              Prestazioni, recensioni e puntualità di ogni professionista interno
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-[11px] text-foreground/60 mb-1">Da</label>
            <input
              value={from} onChange={(e) => setFrom(maskDate(e.target.value))} placeholder="gg/mm/aaaa"
              inputMode="numeric" data-testid="staff-analytics-from"
              className="w-32 px-3 py-1.5 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div>
            <label className="block text-[11px] text-foreground/60 mb-1">A</label>
            <input
              value={to} onChange={(e) => setTo(maskDate(e.target.value))} placeholder="gg/mm/aaaa"
              inputMode="numeric" data-testid="staff-analytics-to"
              className="w-32 px-3 py-1.5 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <Button className="rounded-full h-9 px-4" onClick={() => setRange({ from, to })} data-testid="staff-analytics-apply">
            Applica
          </Button>
          <Button
            variant="outline" className="rounded-full h-9 px-4" data-testid="staff-analytics-reset"
            onClick={() => { const f = sixMonthsAgo(); const x = fmtIt(new Date()); setFrom(f); setTo(x); setRange({ from: f, to: x }); }}
          >
            Azzera
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-foreground/60">Caricamento analytics organico…</p>
      ) : !data || (data.items || []).length === 0 ? (
        <p className="text-sm text-foreground/60" data-testid="staff-analytics-empty">
          Nessun professionista in organico: aggiungilo dalla sezione Organico per vedere le statistiche.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5" data-testid="staff-analytics-totals">
            <div className="border border-border rounded-xl p-3">
              <p className="text-[11px] uppercase tracking-wider text-foreground/60">Assegnate</p>
              <p className="text-xl font-heading font-bold">{t.assigned}</p>
            </div>
            <div className="border border-border rounded-xl p-3">
              <p className="text-[11px] uppercase tracking-wider text-foreground/60">In programma</p>
              <p className="text-xl font-heading font-bold text-sky-700">{t.upcoming}</p>
            </div>
            <div className="border border-border rounded-xl p-3">
              <p className="text-[11px] uppercase tracking-wider text-foreground/60">Concluse</p>
              <p className="text-xl font-heading font-bold text-emerald-700">{t.completed}</p>
            </div>
            <div className="border border-border rounded-xl p-3">
              <p className="text-[11px] uppercase tracking-wider text-foreground/60">Media recensioni</p>
              <p className="text-xl font-heading font-bold">{t.rating_avg ?? '—'}</p>
            </div>
            <div className="border border-border rounded-xl p-3">
              <p className="text-[11px] uppercase tracking-wider text-foreground/60">Puntualità</p>
              <p className="text-xl font-heading font-bold">{t.punctuality_pct !== null ? `${t.punctuality_pct}%` : '—'}</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="staff-analytics-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-foreground/60 border-b border-border">
                  <th className="py-2 pr-3">Professionista</th>
                  <th className="py-2 px-2 text-center">Assegnate</th>
                  <th className="py-2 px-2 text-center">In programma</th>
                  <th className="py-2 px-2 text-center">Concluse</th>
                  <th className="py-2 px-2 text-center">Annullate</th>
                  <th className="py-2 px-2 text-center">Recensioni</th>
                  <th className="py-2 px-2 text-center">Puntualità</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((s) => (
                  <tr key={s.professional_id} className="border-b border-border/50" data-testid={`staff-analytics-row-${s.professional_id}`}>
                    <td className="py-3 pr-3">
                      <div className="font-medium">{s.name}</div>
                      <div className="text-xs text-foreground/60">
                        {s.profession_code}
                        {!s.internal_active && ' · disattivato'}
                      </div>
                    </td>
                    <td className="text-center">{s.assigned}</td>
                    <td className="text-center text-sky-700">{s.upcoming}</td>
                    <td className="text-center text-emerald-700">{s.completed}</td>
                    <td className="text-center">{s.cancelled}</td>
                    <td className="text-center">
                      {s.rating_avg ? (
                        <span className="inline-flex items-center gap-1">
                          <Star size={13} weight="fill" className="text-amber-500" />
                          {s.rating_avg} <span className="text-foreground/50">({s.reviews_count})</span>
                        </span>
                      ) : '—'}
                    </td>
                    <td className="text-center">
                      <Badge className={`${punctualityTone(s.punctuality_pct)} border-0`}>
                        {s.punctuality_pct !== null && s.punctuality_pct !== undefined ? `${s.punctuality_pct}%` : '—'}
                      </Badge>
                      {s.auto_closed > 0 && (
                        <div className="text-[11px] text-foreground/50 mt-0.5">{s.auto_closed} chiuse dal sistema</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-foreground/50 mt-3">
            Puntualità = percentuale di prestazioni concluse dal Centro entro 24 ore dall'orario previsto.
            Quelle chiuse automaticamente dal sistema contano come non puntuali.
          </p>
        </>
      )}
    </Card>
  );
};

export default CenterStaffAnalytics;
